import { captureException } from "@sentry/react"
import { capitalCase } from "change-case"
import { ClassValue, clsx } from "clsx"
import type { JSX } from "preact"
import { Dispatch, useEffect, useState } from "preact/compat"
import { twMerge } from "tailwind-merge"
import { useSearch } from "wouter-preact/use-location"
import { StateUpdater } from "preact/hooks"
import { formatISO } from "date-fns"
import type { DeductionKind } from "@/api/deduction.tsx"
import { to_reporting_amount } from "./currency"
import { GUAYAKI_ORG_ID } from "./guayaki-org"
import { display_coding_amount, display_transaction_amount } from "./transaction_amount"

export * from "./type"
export * from "./copy"
export * from "./download"
export * from "./transaction_amount"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function toISODateString(date: Date) {
  return formatISO(date).substring(0, 10)
}

export function map_or<T, U>(value: T | undefined, default_: U, f: (value: T) => U): U {
  return value === undefined ? default_ : f(value)
}

export function map<T, U>(value: T | undefined | null, f: (value: T) => U): U | undefined {
  return value == undefined ? undefined : f(value)
}

export function classes(...args: (string | JSX.SignalLike<string | undefined> | undefined | boolean)[]) {
  return args.filter(Boolean).join(" ")
}

export function useAsyncEffect(effect: () => Promise<any>, deps: any[] = []) {
  return useEffect(() => safeExec(effect), deps)
}

export function usePageTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} | MarginWiz` : "MarginWiz"
  }, [title])
}
export function unreachable(value: never) {
  console.error(`Unreachable code reached with value: ${value}`)
}
export function noProp(effect: () => void) {
  return (e: Event) => {
    e.stopImmediatePropagation()
    e.preventDefault()
    effect()
  }
}

interface UseFetch<T> {
  loading: boolean
  error: Error | null
  data: T
  refetch: () => Promise<void>
  setData: Dispatch<StateUpdater<T>>
}

export function useFetch<T>(request: () => Promise<T>, deps?: any[], default_value?: undefined): UseFetch<T | undefined>

export function useFetch<T>(request: () => Promise<T>, deps: any[], default_value: T): UseFetch<T>

export function useFetch<T>(request: () => Promise<T>, deps: any[] = [], default_value: T): UseFetch<T> {
  let [data, setData] = useState(default_value)
  let [loading, setLoading] = useState(true)
  let [error, setError] = useState<Error | null>(null)

  async function refetch() {
    setLoading(true)
    setError(null)
    try {
      const result = await request()
      setData(result)
    } catch (error) {
      setError(error as Error)
    } finally {
      setLoading(false)
    }
  }

  useAsyncEffect(refetch, deps)
  return { data, loading, error, refetch, setData }
}

export function safeExec(action: () => Promise<any>) {
  action().catch(e => {
    captureException(e)
    console.error(e)
  })
}

export function useSearchParams<T extends object>(): T {
  let search_string: string = useSearch()
  let search_params = new URLSearchParams(search_string)
  return Object.fromEntries(search_params.entries()) as T
}

export function displayFormattedImportSource(import_source: string): string {
  if (!import_source) return ""
  // Remove 'csv' suffix and capitalize
  const withoutCsv = import_source.replace(/csv$/, "")
  return capitalCase(withoutCsv)
}

export interface DisplayNameUserLike {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}

export function get_email_local_part(email: string | null | undefined): string {
  if (!email) return ""
  const [local_part] = email.split("@")
  return local_part || ""
}

export function get_display_name(user: DisplayNameUserLike | string | null | undefined): string {
  if (!user) return ""

  if (typeof user === "string") {
    return get_email_local_part(user)
  }

  const first_name = user.first_name?.trim()
  const last_name = user.last_name?.trim()

  if (first_name && last_name) {
    return `${first_name} ${last_name}`
  }

  if (first_name) {
    return first_name
  }

  return get_email_local_part(user.email)
}

export function displayFormattedDistributor(source: string, original_source?: string): string {
  if (!source) return ""
  if (source.toLowerCase() === "other" && original_source) {
    return formatOriginalDistributor(original_source)
  }
  return formatDistributor(source)
}

export function formatDistributor(name: string): string {
  if (!name) return ""
  const lowerName = name.toLowerCase()
  if (lowerName.includes("kehe")) {
    return "KeHE"
  } else if (lowerName.includes("unfi")) {
    return "UNFI"
  } else if (lowerName.includes("cands")) {
    return "C&S"
  } else if (lowerName.includes("heb")) {
    return "HEB"
  } else if (lowerName.includes("albertsons")) {
    return "Albertson's"
  } else if (lowerName.includes("demoulas")) {
    return "Demoula's"
  } else if (lowerName.includes("cvs")) {
    return "CVS"
  } else if (lowerName.includes("geyser")) {
    return "Big Geyser"
  } else if (lowerName.includes("svharbor") || lowerName.includes("sv_harbor")) {
    return "SV Harbor"
  } else if (lowerName.includes("dollargeneral") || lowerName.includes("dollar general")) {
    return "Dollar General"
  } else if (lowerName.includes("coremark") || lowerName.includes("core-mark")) {
    return "Core-Mark"
  } else if (lowerName.includes("mclane")) {
    return "McLane"
  } else if (lowerName.includes("vistar")) {
    return "Vistar"
  } else if (lowerName.includes("fivebelow") || lowerName.includes("five below")) {
    return "Five Below"
  } else if (lowerName.includes("holland")) {
    return "Holland & Barrett"
  } else {
    return capitalCase(name)
  }
}

function formatOriginalDistributor(original_source: string): string {
  if (!original_source) return ""

  const lowerName = original_source.toLowerCase()
  if (lowerName.includes("bozzuto")) {
    return "Bozzuto's"
  }

  // Filter out any leading numbers and join the remaining words for the original source that comes from super's accounting system
  const words = original_source.split(" ").filter(word => isNaN(Number(word)))
  return capitalCase(words.join(" "))
}

const FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})
const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
})
export const FULL_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
})

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>([["USD", FORMATTER]])

/** A cached `Intl` currency formatter for an ISO 4217 code; dollars for a missing or unknown code. */
function currency_format(currency?: string | null): Intl.NumberFormat {
  const code = (currency || "USD").toUpperCase()
  let formatter = CURRENCY_FORMATTERS.get(code)
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: code })
    } catch {
      formatter = FORMATTER
    }
    CURRENCY_FORMATTERS.set(code, formatter)
  }
  return formatter
}

// whole amounts, no cents (dollars unless a currency is given)
export function currencyFormatter(number: number, currency?: string | null) {
  const formatted = currency_format(currency).format(Math.round(number)).toString()
  return formatted.replace(/\.00$/, "")
}

/** `$1,234.56`, or `£1,234.56` for a row in sterling — a row's amounts are always in its own currency. */
export function exactCurrencyFormatter(number: number, currency?: string | null) {
  return currency_format(currency).format(number)
}

export function format_transaction_amount(kind: DeductionKind, amount: number, currency?: string | null): string {
  return exactCurrencyFormatter(display_transaction_amount(kind, amount), currency)
}

export { to_reporting_amount } from "./currency"
export type { CurrencyFields } from "./currency"

const RATE_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

export function rateFormatter(number: number) {
  return RATE_FORMATTER.format(number)
}

export const dateFormatter = function (date: string | undefined, formatter = DATE_FORMATTER): string {
  if (!date) {
    return ""
  }
  try {
    const parsedDate = new Date(date)
    const timezoneOffset = parsedDate.getTimezoneOffset() * 60000 // offset in milliseconds
    const adjustedDate = new Date(parsedDate.getTime() + timezoneOffset)
    return formatter.format(adjustedDate)
  } catch (e) {
    console.warn(`Could not format data as date: ${date}`)
    return date
  }
}

export function index_map<T>(values: string[]): Record<string, number> {
  return Object.fromEntries(values.map((header, index) => [header, index]))
}

export function stopPropagation(event: Event) {
  event.preventDefault()
  event.stopImmediatePropagation()
}

export function isSuperCoffeeUser(orgId: string | null | undefined): boolean {
  const SUPER_COFFEE_ORG_IDS = ["01910f2d-42da-6dfe-8709-9c52567fbb12", "019a5c42-8503-9934-8db9-21210f6f5116"]
  return !!orgId && SUPER_COFFEE_ORG_IDS.includes(orgId)
}

export function isGuayakiUser(orgId: string | null | undefined): boolean {
  return orgId === GUAYAKI_ORG_ID
}

const LAURA_CHENEL_ORG_ID = "019c7dd5-4998-a19e-ea3a-87cc3cf7d507"

export function isLauraChenelUser(orgId: string | null | undefined): boolean {
  return orgId === LAURA_CHENEL_ORG_ID
}

const SAFFRON_ROAD_ORG_ID = "019e0578-2890-37aa-7c0c-2fd9fde8d2bb"

export function isSaffronRoadUser(orgId: string | null | undefined): boolean {
  return orgId === SAFFRON_ROAD_ORG_ID
}

if (import.meta.vitest) {
  describe("currency formatting", () => {
    it("formats dollars by default, unchanged from before", () => {
      expect(exactCurrencyFormatter(1094.65)).toBe("$1,094.65")
      expect(currencyFormatter(1234.56)).toBe("$1,235")
      expect(currencyFormatter(-1234.4)).toBe("-$1,234")
    })

    it("formats a row in its own currency", () => {
      expect(exactCurrencyFormatter(-35564.45, "GBP")).toBe("-£35,564.45")
      expect(exactCurrencyFormatter(10, "CAD")).toBe("CA$10.00")
      expect(currencyFormatter(7844.52, "GBP")).toBe("£7,845")
      expect(exactCurrencyFormatter(5, "not-a-code")).toBe("$5.00")
    })

    it("converts a non-dollar row at its own rate for totals", () => {
      expect(to_reporting_amount({ currency: "GBP", reporting_fx_rate: 1.3 }, 100)).toBeCloseTo(130)
      expect(to_reporting_amount({ currency: "USD", reporting_fx_rate: 9 }, 100)).toBe(100)
      expect(to_reporting_amount({ currency: "CAD", reporting_fx_rate: null }, 100)).toBe(100)
      expect(to_reporting_amount({}, 100)).toBe(100)
    })

    it("names Holland & Barrett", () => {
      expect(formatDistributor("holland_barrett")).toBe("Holland & Barrett")
    })
  })

  describe("get_display_name", () => {
    it("prefers first name when present", () => {
      expect(get_display_name({ first_name: "Joey", email: "joey@marginwiz.com" })).toBe("Joey")
    })

    it("uses first and last name when both exist", () => {
      expect(
        get_display_name({ first_name: "Joey", last_name: "Smith", email: "joey.smith@marginwiz.com" })
      ).toBe("Joey Smith")
    })

    it("falls back to email local part", () => {
      expect(get_display_name({ email: "person@example.com" })).toBe("person")
      expect(get_display_name("other.person@example.com")).toBe("other.person")
    })

    it("returns empty string for missing values", () => {
      expect(get_display_name(undefined)).toBe("")
      expect(get_display_name({})).toBe("")
      expect(get_display_name({ email: "" })).toBe("")
    })
  })

  describe("formatDistributor", () => {
    it('should return "KeHE" for names including "kehe"', () => {
      expect(formatDistributor("kehe distributors")).toBe("KeHE")
      expect(formatDistributor("KEHE")).toBe("KeHE")
    })

    it('should return "UNFI" for names including "unfi"', () => {
      expect(formatDistributor("unfi wholesale")).toBe("UNFI")
      expect(formatDistributor("UNFI")).toBe("UNFI")
    })

    it("should return capitalized case for other names", () => {
      expect(formatDistributor("other distributor")).toBe("Other Distributor")
    })

    it("should return an empty string for empty input", () => {
      expect(formatDistributor("")).toBe("")
    })
  })

  describe("classes", () => {
    it("should join non-empty string arguments", () => {
      expect(classes("class1", "class2", "class3")).toBe("class1 class2 class3")
    })

    it("should filter out falsy values", () => {
      // @ts-ignore
      expect(classes("class1", undefined, "class2", null, "class3", false, "")).toBe("class1 class2 class3")
    })

    it("should return an empty string when all arguments are falsy", () => {
      // @ts-ignore
      expect(classes(undefined, null, false, "")).toBe("")
    })
  })

  describe("formatOriginalDistributor", () => {
    it("should remove leading numbers and spaces", () => {
      expect(formatOriginalDistributor("650 McLane Co.")).toBe("Mc Lane Co")
      expect(formatOriginalDistributor("123 ABC Company")).toBe("Abc Company")
    })

    it("should handle strings without leading numbers", () => {
      expect(formatOriginalDistributor("McLane Co.")).toBe("Mc Lane Co")
      expect(formatOriginalDistributor("Simple Company")).toBe("Simple Company")
    })

    it("should return an empty string for empty input", () => {
      expect(formatOriginalDistributor("")).toBe("")
    })

    it("should handle multiple leading numbers", () => {
      expect(formatOriginalDistributor("123 456 Company Name")).toBe("Company Name")
    })
  })

  // The Amount column on the check detail rows renders exactly this composition, so assert on the
  // formatted string an early-pay row shows — a discount is a cost and must read like the deduction
  // rows around it, and a missing discount must not render as "-$0.00".
  describe("exactCurrencyFormatter(display_coding_amount(...))", () => {
    function coding_cell(kind: DeductionKind, invoice_amount: number, discount_amount: number | null): string {
      return exactCurrencyFormatter(display_coding_amount(kind, invoice_amount, discount_amount))
    }

    it("shows a KeHE early-pay discount as a negative", () => {
      // KeHE check 1368083, row SI00003704: $44.15 booked to 5042 Early Payment Discounts.
      expect(coding_cell("invoice", 2696.8, 44.15)).toBe("-$44.15")
      expect(coding_cell("invoice", 2696.8, -44.15)).toBe("-$44.15")
    })

    it("shows no sign when an invoice has no discount", () => {
      expect(coding_cell("invoice", 2696.8, null)).toBe("$0.00")
    })

    it("leaves the other check rows alone", () => {
      expect(coding_cell("deduction", -300, null)).toBe("-$300.00")
      expect(coding_cell("deduction", 67.2, null)).toBe("$67.20")
    })
  })
}

// Basic copy function without toast - deprecated, use copy utilities from ./copy instead
export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(err => {
    console.error("Failed to copy: ", err)
  })
}
